import { PlatformEnums } from 'utils/platforms';
import servicePath from '@/routes/services/servicePath';
import {
    ZOOM,
    ZOOM_AUTH,
    EIGHTBYEIGHT,
    EIGHTBYEIGHT_AUTH,
    RINGCENTRAL,
    RINGCENTRAL_AUTH,
    DIALPAD,
    DIALPAD_AUTH,
    WEBEX,
    WEBEX_AUTH,
} from 'pages/dashboards/v5/constants';

import zoomCircleLogo from 'assets/images/zoom-tcap.svg';
import eightByEightLogo from 'assets/images/8x8-tcap.svg';
import ringCentralLogo from 'assets/images/ring-central.svg';
import dialPadLogo from 'assets/images/dialpad.png';
import webexLogo from 'assets/images/webex.svg';

/**
 * Single source of truth for platforms whose auth flow is "redirect to a login
 * link and nothing else" and whose post-creation flow is the shared `serviceFlow`
 * state machine in useServiceWizard.js (see `serviceFlow`/`onServiceCreate`).
 *
 * Keyed by the *created service's* serviceType (the value `onServiceCreate` sees
 * on `serviceResponse.serviceType`). Zoom is the one platform here whose wizard
 * selection uses a different serviceType (`ZOOM` = 'ZoomPEX') than the created
 * service (`ZOOM_AUTH` = 'Zoom') — see `wizardServiceType` below.
 *
 * This list intentionally excludes anything with its own tenant/selective-sync
 * state machine (MicrosoftGraph/OperatorConnect/DirectRouting/GCC High) or a
 * bespoke creation flow (SBC, TeamsPhoneMobile, OperatorPlatform, VendorDefined) —
 * those keep their existing hand-written logic; only genuinely identical
 * "redirect to a login link" platforms belong here.
 *
 * Deliberately pure data (no API-calling functions) — this module is imported
 * by presentational/status code (CardDetails, statusReducer, dashboard cards)
 * that must stay testable without pulling in services/axios. useServiceWizard.js
 * is the only place that pairs each platform's `key` with its real
 * getXLoginLink API function (see SIMPLE_REDIRECT_LOGIN_LINK_FNS there).
 */
export const SIMPLE_REDIRECT_AUTH_PLATFORMS = {
    Zoom: {
        key: 'Zoom',
        id: PlatformEnums.ZOOM,
        serviceType: ZOOM_AUTH, // 'Zoom' — the created service's serviceType
        wizardServiceType: ZOOM, // 'ZoomPEX' — the service-profile's serviceType at selection time
        authServiceType: ZOOM_AUTH,
        appAuthTypeID: 'Zoom',
        displayName: 'Zoom',
        createServiceName: 'Zoom',
        icon: zoomCircleLogo,
        routePath: undefined,
        routePathAuth: servicePath.SERVICES_ZOOM_AUTH,
        wizardDescription:
            'Authorize your Zoom service to connect using Zoom Provider Exchange and activate Zoom Phone services',
    },
    EightByEight: {
        key: 'EightByEight',
        id: PlatformEnums.EIGHTBYEIGHT,
        serviceType: EIGHTBYEIGHT,
        wizardServiceType: EIGHTBYEIGHT,
        authServiceType: EIGHTBYEIGHT_AUTH,
        appAuthTypeID: 'EightByEight',
        displayName: '8x8',
        createServiceName: 'Eight By Eight',
        icon: eightByEightLogo,
        routePath: undefined,
        routePathAuth: servicePath.SERVICES_EIGHTBYEIGHT_AUTH,
        wizardDescription:
            'Authorize your 8x8 service to connect using 8x8 Provider Exchange and activate 8x8 services',
    },
    RingCentral: {
        key: 'RingCentral',
        id: PlatformEnums.RINGCENTRAL,
        serviceType: RINGCENTRAL,
        wizardServiceType: RINGCENTRAL,
        authServiceType: RINGCENTRAL_AUTH,
        appAuthTypeID: 'RingCentral',
        displayName: 'Ring Central',
        createServiceName: 'Ring Central',
        icon: ringCentralLogo,
        routePath: servicePath.SERVICES_RINGCENTRAL,
        routePathAuth: servicePath.SERVICES_RINGCENTRAL_AUTH,
        walkthroughKey: 'ringcentral',
        wizardDescription:
            'Authorize your RingCentral service to connect using BYOC. Fully automate number and user management.',
    },
    DialPad: {
        key: 'DialPad',
        id: PlatformEnums.DIALPAD,
        serviceType: DIALPAD,
        wizardServiceType: DIALPAD,
        authServiceType: DIALPAD_AUTH,
        appAuthTypeID: 'DialPad',
        displayName: 'Dial Pad',
        createServiceName: 'Dial Pad',
        icon: dialPadLogo,
        routePath: servicePath.SERVICES_DIALPAD,
        routePathAuth: servicePath.SERVICES_DIALPAD_AUTH,
        walkthroughKey: 'dialpad',
        wizardDescription:
            'Authorize your Dial Pad service to connect using BYOC and activate Dial Pad services',
    },
    Webex: {
        key: 'Webex',
        id: PlatformEnums.WEBEX,
        serviceType: WEBEX,
        wizardServiceType: WEBEX,
        authServiceType: WEBEX_AUTH,
        appAuthTypeID: 'WebexAppAuth',
        displayName: 'Webex',
        createServiceName: 'Webex',
        icon: webexLogo,
        routePath: undefined,
        routePathAuth: servicePath.SERVICES_WEBEX_AUTH,
        wizardDescription:
            'Authorize your Webex tenant to activate Webex Calling services, with fully automated location and trunk management.',
    },
};

const BY_SERVICE_TYPE = Object.fromEntries(
    Object.values(SIMPLE_REDIRECT_AUTH_PLATFORMS).map((p) => [
        p.serviceType,
        p,
    ]),
);
const BY_WIZARD_SERVICE_TYPE = Object.fromEntries(
    Object.values(SIMPLE_REDIRECT_AUTH_PLATFORMS).map((p) => [
        p.wizardServiceType,
        p,
    ]),
);
const BY_AUTH_SERVICE_TYPE = Object.fromEntries(
    Object.values(SIMPLE_REDIRECT_AUTH_PLATFORMS).map((p) => [
        p.authServiceType,
        p,
    ]),
);

export const SIMPLE_REDIRECT_SERVICE_TYPES = Object.keys(BY_SERVICE_TYPE);
export const SIMPLE_REDIRECT_AUTH_SERVICE_TYPES =
    Object.keys(BY_AUTH_SERVICE_TYPE);

/** Of the simple-redirect auth platforms, only these two surface a distinct
 * "Pending Access" status ahead of Healthy/Error — Zoom/8x8/Webex go straight
 * to Access Required/Degraded/Error (see statusReducer.js). */
const PENDING_ACCESS_PLATFORMS = [
    SIMPLE_REDIRECT_AUTH_PLATFORMS.RingCentral,
    SIMPLE_REDIRECT_AUTH_PLATFORMS.DialPad,
];

export const PENDING_ACCESS_AUTH_SERVICE_TYPES = PENDING_ACCESS_PLATFORMS.map(
    (p) => p.authServiceType,
);

/** Returns the matching platform (RingCentral or DialPad) when `serviceType`
 * is either its "service" or "auth" serviceType constant, else undefined. */
export const getPendingAccessPlatform = (serviceType) =>
    PENDING_ACCESS_PLATFORMS.find(
        (p) =>
            p.serviceType === serviceType || p.authServiceType === serviceType,
    );

export const getSimpleRedirectPlatformByServiceType = (serviceType) =>
    BY_SERVICE_TYPE[serviceType];

export const getSimpleRedirectPlatformByWizardServiceType = (serviceType) =>
    BY_WIZARD_SERVICE_TYPE[serviceType];

export const getSimpleRedirectPlatformByAuthServiceType = (serviceType) =>
    BY_AUTH_SERVICE_TYPE[serviceType];

const BY_APP_AUTH_TYPE_ID = Object.fromEntries(
    Object.values(SIMPLE_REDIRECT_AUTH_PLATFORMS).map((p) => [
        p.appAuthTypeID,
        p,
    ]),
);

/** Translate the API's `appAuthTypeID` (e.g. 'RingCentral', 'EightByEight')
 * into the dashboard status-context key it's stored under (its authServiceType,
 * e.g. RINGCENTRAL_AUTH). Returns undefined if `appAuthTypeID` doesn't belong
 * to a simple-redirect platform. */
export const getSimpleRedirectAuthServiceTypeByAppAuthTypeID = (
    appAuthTypeID,
) => BY_APP_AUTH_TYPE_ID[appAuthTypeID]?.authServiceType;

export const isSimpleRedirectServiceType = (serviceType) =>
    Object.prototype.hasOwnProperty.call(BY_SERVICE_TYPE, serviceType);

export const isSimpleRedirectAuthServiceType = (serviceType) =>
    Object.prototype.hasOwnProperty.call(BY_AUTH_SERVICE_TYPE, serviceType);

/**
 * The two platforms proven byte-for-byte identical to each other end-to-end
 * (dashboard card, auth card, service-profile form, wizard step, user-edit
 * drawer placeholder): RingCentral and DialPad. These are the only two platforms
 * currently backed by the Generic* components in this codebase — Zoom/8x8/Webex
 * keep their existing bespoke service-profile forms and wizard step cards (they
 * have real per-platform fields/logic not present here), see ADDING_A_PLATFORM.md.
 */
export const GENERIC_SIMPLE_SERVICE_PROFILE_PLATFORMS = {
    RingCentral: SIMPLE_REDIRECT_AUTH_PLATFORMS.RingCentral,
    DialPad: SIMPLE_REDIRECT_AUTH_PLATFORMS.DialPad,
};

export const getGenericSimpleServiceProfilePlatform = (serviceType) =>
    Object.values(GENERIC_SIMPLE_SERVICE_PROFILE_PLATFORMS).find(
        (p) => p.serviceType === serviceType,
    );

export const isGenericSimpleServiceProfileServiceType = (serviceType) =>
    Object.values(GENERIC_SIMPLE_SERVICE_PROFILE_PLATFORMS).some(
        (p) => p.serviceType === serviceType,
    );

export const getGenericSimpleServiceProfilePlatformById = (id) =>
    Object.values(GENERIC_SIMPLE_SERVICE_PROFILE_PLATFORMS).find(
        (p) => p.id === id,
    );

/**
 * Platform families with real, bespoke logic that this consolidation pass does
 * NOT rewrite — listed here only so the registry is a complete map of every
 * platform in the app (see ADDING_A_PLATFORM.md for why each is excluded from
 * the generic/simple-redirect groups above).
 */
export const TENANT_FAMILY_PLATFORMS = {
    MicrosoftGraph: {
        id: PlatformEnums.TEAMS,
        variants: ['Microsoft', 'OperatorConnect', 'DirectRouting'],
        note: 'Shares one Microsoft tenant AppAuth; ocFlow/drFlow state machines branch on selectiveSyncPolicy x authPolicy x tenant status. See useServiceWizard.js.',
    },
    GCCHigh: {
        id: PlatformEnums.GCC,
        variants: ['DirectRoutingGCCH'],
        note: 'Same Microsoft tenant-family shape as above but its own AppAuth record (platformID-disambiguated) and its own service-profile form (name, visibleScope, dial plans, regions, voice policies — cascading fetch).',
    },
};

export const STANDALONE_DASHBOARD_CARD_PLATFORMS = {
    ACS: { id: PlatformEnums.AZURE },
    TPM: { id: undefined },
    SBC: { id: PlatformEnums.SBC },
    SRT: {
        id: undefined,
        note: 'Modern SBCaaS. Same bespoke shape as SBC — no redirect auth, its own service-profile form (max channels, regions), trunk pages and dashboard card. Not a simple-redirect platform.',
    },
    VendorDefined: { id: undefined },
    OperatorPlatform: { id: PlatformEnums.OPERATOR_PLATFORM },
};
